import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, ShieldCheck, Star, MapPin } from "lucide-react";
import { SITE } from "@/lib/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1625585598750-3535fe40efb3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=2000";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 2.6 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  return (
    <section id="top" className="relative min-h-[100vh] w-full overflow-hidden bg-white pt-20">
      {/* Background image with elegant overlay */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Helle, aufgeräumte Räume nach Haushaltsauflösung"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy/85 via-navy/65 to-navy/40" style={{ background: "linear-gradient(135deg, rgba(30,58,95,0.92) 0%, rgba(30,58,95,0.70) 50%, rgba(30,58,95,0.35) 100%)" }} />
        <div className="absolute inset-0" style={{
          background: "radial-gradient(circle at 80% 20%, rgba(78,168,58,0.18), transparent 50%)"
        }} />
      </div>

      <motion.div
        variants={stagger}
        initial="hidden"
        animate="show"
        className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24 md:pb-32"
      >
        <motion.h2
          variants={fadeUp}
          className="font-heading mt-6 text-white text-6xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-none"
          style={{ letterSpacing: "-0.03em" }}
        >
          Scharpf
        </motion.h2>
        <motion.div
          variants={fadeUp}
          className="mt-1 text-white/70 text-sm md:text-base tracking-[0.28em] uppercase font-semibold"
        >
          Haushaltsauflösungen · Entrümpelungen
        </motion.div>

        <motion.h1
          variants={fadeUp}
          className="font-heading mt-5 text-white text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] max-w-5xl"
        >
          Haushaltsauflösungen,
          <br />
          Entrümpelungen &{" "}
          <span className="text-shine">Goldankauf</span>
          <br />
          in Oberhausen.
        </motion.h1>

        <motion.p
          variants={fadeUp}
          className="mt-7 text-white/85 text-lg md:text-xl max-w-2xl leading-relaxed"
        >
          Ihr zuverlässiger Partner für Haushaltsauflösungen, Nachlassankäufe, Entrümpelungen und den Ankauf
          von Gold, Schmuck und Edelmetallen. Persönlich, fair und professionell.
        </motion.p>

        <motion.div variants={fadeUp} className="mt-6 inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20">
          <div className="w-11 h-11 rounded-xl bg-white p-1 flex items-center justify-center flex-shrink-0">
            <img src={SITE.logo} alt="Scharpf Logo" className="w-full h-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="text-white/70 text-[11px] uppercase tracking-[0.22em] font-bold">Inhabergeführt</div>
            <div className="text-white font-heading text-lg font-extrabold">Kevin Scharpf</div>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="mt-9 flex flex-wrap items-center gap-4">
          <a
            href="#kontakt"
            data-testid="hero-cta-button"
            className="group inline-flex items-center gap-3 px-7 py-4 rounded-full text-white text-base md:text-lg font-bold shadow-xl shadow-green-900/20 hover:-translate-y-0.5 hover:shadow-2xl transition-all"
            style={{ backgroundColor: "#4ea83a" }}
          >
            Kostenlose Besichtigung anfragen
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href={`tel:${SITE.phoneE164}`}
            data-testid="hero-call-button"
            className="inline-flex items-center gap-3 px-6 py-4 rounded-full bg-white/10 backdrop-blur-md border border-white/25 text-white font-semibold hover:bg-white/15 transition-colors"
          >
            <Phone className="w-5 h-5" />
            {SITE.phoneDisplay}
          </a>
        </motion.div>

        {/* Trust strip */}
        <motion.div
          variants={fadeUp}
          className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl"
        >
          {[
            { icon: ShieldCheck, label: "Kostenlose Besichtigung" },
            { icon: Star, label: "Festpreis-Garantie" },
            { icon: ShieldCheck, label: "Fachgerechte Entsorgung" },
            { icon: Star, label: "Besenreine Übergabe" },
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15"
            >
              <t.icon className="w-5 h-5 flex-shrink-0" style={{ color: "#7dd960" }} />
              <span className="text-white text-sm font-medium">{t.label}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Bottom curved fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
}
